import {
  BIOMES,
  BOSSES,
  type ContractsEvent,
  type ContractView,
  isBossId,
  getItem,
  STREAK_PET_DAY,
  STREAK_PET_ITEM,
  streakReward,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { contractHint, contractRewardLine, contractTitle, countdown } from './contract-text.js';
import { element, kitIcon } from './kit.js';
import { itemSlot } from './quest-rewards.js';
import { npcName } from './quest-text.js';
import { uiFormat, uiText } from './text.js';

/**
 * The contracts' tab of the journal (ADR 0051): the day's three and the
 * week's one, each with its count, what it pays and « Encaisser » once it is
 * filled, and how long until the next ones. Only a view of what the server
 * sent; cashing is an intention.
 */

export interface ContractBoardOptions {
  icons: () => CssAtlas | null;
  onClaim: (contractId: ContractView['id']) => void;
}

export class ContractBoard {
  readonly root = element('div', 'contracts');
  private state: ContractsEvent | undefined;
  /** When the state arrived, for the countdowns. */
  private receivedAt = 0;
  private timer: number | undefined;

  constructor(private readonly options: ContractBoardOptions) {
    this.render();
  }

  update(state: ContractsEvent): void {
    this.state = state;
    this.receivedAt = performance.now();
    this.render();
  }

  /** Contracts filled and not yet cashed: the tab says how many. */
  get ready(): number {
    const state = this.state;
    if (state === undefined) return 0;
    return [...state.daily, state.weekly].filter((c) => !c.claimed && c.progress >= c.count).length;
  }

  /** The countdowns tick only while the tab is shown. */
  setVisible(visible: boolean): void {
    if (this.timer !== undefined) window.clearInterval(this.timer);
    this.timer = visible
      ? window.setInterval(() => {
          this.render();
        }, 30_000)
      : undefined;
    if (visible) this.render();
  }

  private render(): void {
    this.root.replaceChildren();
    const state = this.state;
    if (state === undefined) {
      this.root.append(element('p', 'smith__muted', 'Les contrats arrivent…'));
      return;
    }
    const elapsed = performance.now() - this.receivedAt;
    this.root.append(
      this.streak(state.streak),
      this.section('Contrats du jour', state.dayEndsInMs - elapsed),
      ...state.daily.map((contract) => this.card(contract)),
      this.section('Contrat de la semaine', state.weekEndsInMs - elapsed),
      this.card(state.weekly),
      this.bosses(state.bossBonus),
    );
  }

  private section(title: string, endsInMs: number): HTMLElement {
    const head = element('h3', 'contracts__section', title);
    head.append(
      element(
        'span',
        'contracts__renew',
        uiFormat(' · nouveaux dans {time}', { time: countdown(endsInMs) }),
      ),
    );
    return head;
  }

  /**
   * The account's streak: days counted, whether today is, what the next day
   * pays, and how far the Light Fox is.
   */
  private streak(streak: ContractsEvent['streak']): HTMLElement {
    const box = element('div', 'contracts__streak');
    const head = element('h3', 'contracts__section', 'Série de connexions');
    head.append(
      element(
        'span',
        'contracts__renew',
        streak.days > 1
          ? uiFormat(' · {days} jours', { days: streak.days })
          : uiFormat(' · {days} jour', { days: streak.days }),
      ),
    );
    box.append(head);
    if (streak.days < STREAK_PET_DAY) box.append(this.petGoal(streak.days));
    const next = streakReward(streak.days + 1);
    box.append(
      element(
        'p',
        'contracts__hint',
        streak.today
          ? uiFormat('Aujourd’hui est compté. Demain : {reward}.', {
              reward: contractRewardLine(next),
            })
          : uiFormat('Remplis un contrat du jour pour compter aujourd’hui : {reward}.', {
              reward: contractRewardLine(next),
            }),
      ),
    );
    if (streak.days >= STREAK_PET_DAY) box.append(this.petGoal(streak.days));
    return box;
  }

  /**
   * The Light Fox to look forward to: its icon (the item's tooltip on hover),
   * one pip per day of the streak up to the seventh, and how many are left;
   * once earned, a reminder that it is in the bag.
   */
  private petGoal(days: number): HTMLElement {
    const goal = element('div', 'contracts__pet');
    if (days >= STREAK_PET_DAY) goal.classList.add('contracts__pet--earned');
    goal.append(itemSlot({ templateId: STREAK_PET_ITEM, quantity: 1 }, this.options.icons()));
    const text = element('div', 'contracts__pet-text');
    const name = getItem(STREAK_PET_ITEM)?.name ?? STREAK_PET_ITEM;
    text.append(element('span', 'contracts__pet-name', name));
    const pips = element('span', 'contracts__pips');
    for (let day = 1; day <= STREAK_PET_DAY; day += 1) {
      const pip = element('span', 'contracts__pip');
      if (day <= days) pip.classList.add('contracts__pip--on');
      if (day === STREAK_PET_DAY) pip.classList.add('contracts__pip--goal');
      pips.append(pip);
    }
    text.append(pips);
    const left = STREAK_PET_DAY - days;
    text.append(
      element(
        'span',
        'contracts__hint',
        left > 0
          ? uiFormat(
              left > 1
                ? 'Encore {left} jours de série pour l’adopter ({days}/{goal}).'
                : 'Encore {left} jour de série pour l’adopter ({days}/{goal}).',
              { left, days, goal: STREAK_PET_DAY },
            )
          : 'Adopté : équipe-le comme familier depuis ton sac.',
      ),
    );
    goal.append(text);
    return goal;
  }

  /** The zone bosses whose loot of the day is still to take (ADR 0090), with their region. */
  private bosses(available: readonly string[]): HTMLElement {
    const box = element('div', 'contracts__bosses');
    box.append(element('h3', 'contracts__section', 'Butin des boss'));
    const names = available
      .filter(isBossId)
      .map((id) => `${npcName(id)} (${BIOMES[BOSSES[id].biome].name})`);
    box.append(
      element(
        'p',
        'contracts__hint',
        names.length > 0
          ? uiFormat('Butin du jour encore à prendre : {bosses}.', { bosses: names.join(', ') })
          : 'Tous les boss ont donné leur butin aujourd’hui.',
      ),
    );
    return box;
  }

  private card(contract: ContractView): HTMLElement {
    const done = contract.progress >= contract.count;
    const card = element('div', 'contracts__card');
    if (contract.claimed) card.classList.add('contracts__card--claimed');
    else if (done) card.classList.add('contracts__card--ready');

    const text = element('div', 'contracts__text');
    text.append(element('span', 'contracts__title', contractTitle(contract)));
    const hint = contractHint(contract);
    if (hint !== undefined) text.append(element('span', 'contracts__hint', hint));
    const bar = element('span', 'contracts__bar');
    const fill = element('span', 'contracts__fill');
    fill.style.width = `${String(Math.round((contract.progress / contract.count) * 100))}%`;
    bar.append(fill);
    text.append(
      bar,
      element('span', 'contracts__count', `${String(contract.progress)}/${String(contract.count)}`),
    );

    const reward = element('div', 'contracts__reward');
    if (contract.reward.gold > 0) {
      const gold = element('span', 'quest-rewards__gold', String(contract.reward.gold));
      gold.prepend(kitIcon('gold'));
      reward.append(gold);
    }
    for (const item of contract.reward.items) reward.append(itemSlot(item, this.options.icons()));

    card.append(text, reward);
    if (contract.claimed) {
      card.append(element('span', 'contracts__state', 'Touché'));
    } else if (done) {
      const claim = element('button', 'smith__go contracts__claim', 'Encaisser');
      claim.type = 'button';
      claim.addEventListener('click', () => {
        claim.disabled = true;
        this.options.onClaim(contract.id);
      });
      card.append(claim);
    }
    card.setAttribute('aria-label', uiText(contractTitle(contract)));
    return card;
  }

  destroy(): void {
    if (this.timer !== undefined) window.clearInterval(this.timer);
    this.root.remove();
  }
}
